import React, { useState, useEffect, useRef } from 'react';
import { sessionsApi } from '../../api/sessions';
import Modal from '../Common/Modal';
import Loading from '../Common/Loading';
import './SessionSummaryModal.css';

/**
 * Parse summary content and make [#N] citations clickable.
 * @param {string} content - Summary text with [#N] citations
 * @param {function} onCitationClick - Callback when citation is clicked
 * @param {object} citationMap - Selected summary's {message number: message id}
 * @returns {Array} Array of React elements (text and citation links)
 */
function renderContentWithCitations(content, onCitationClick, citationMap) {
  if (!content) return null;

  const map = (
    citationMap && typeof citationMap === 'object' && !Array.isArray(citationMap)
  ) ? citationMap : {};

  // Pattern to match [#N] citations
  const citationPattern = /\[#(\d+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = citationPattern.exec(content)) !== null) {
    // Add text before the citation
    if (match.index > lastIndex) {
      parts.push(content.substring(lastIndex, match.index));
    }

    // Add the citation as a clickable link
    const messageNumber = parseInt(match[1], 10);
    parts.push(
      <button
        key={`citation-${match.index}`}
        className="session-summary-modal__citation"
        onClick={() => onCitationClick && onCitationClick(messageNumber, map)}
        title={`Go to message #${messageNumber}`}
      >
        [#{messageNumber}]
      </button>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last citation
  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  return parts.length > 0 ? parts : content;
}

/**
 * Format summary label for dropdown
 * @param {object} summary - Summary object with summary_number, created_at, message_count
 * @returns {string} Formatted label
 */
function formatSummaryLabel(summary) {
  const date = new Date(summary.created_at);
  const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  if (summary.summary_number === 0) {
    return `Final Summary (${dateStr} ${timeStr})`;
  }
  return `Summary #${summary.summary_number} (${dateStr} ${timeStr})`;
}

/**
 * Modal to display session summaries with a dropdown to select previous summaries.
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Close handler
 * @param {string} sessionId - Session ID to fetch summaries for
 * @param {function} onCitationClick - Optional callback (messageNumber, citationMap)
 */
/** [#N] markers currently in the edited text that the prior version had —
 *  i.e. citations the save will DETACH (writer removed them). */
function emptySummaryCopy(status) {
  const n = status?.session_message_count || 0;
  if (n >= 15) {
    return 'A summary is created every 15 messages. This one is being written — check again in a moment.';
  }
  return `A summary is created every 15 messages. This session has ${n} so far.`;
}

function detachedCitations(originalContent, editedContent) {
  const markers = (s) => new Set((s.match(/\[#(\d+)\]/g) || []));
  const before = markers(originalContent);
  const after = markers(editedContent);
  return [...before].filter((m) => !after.has(m));
}

function finiteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Header for an unsaved merge review.
 * Only fields the merge response actually carries are shown. The endpoint
 * returns `{ content }` today, and that draft has no saved identity: no
 * number, date, message count, or edited-version badge. The saved head is
 * not a stand-in. A hidden non-gist row shares the number sequence, so
 * "the next interim number" would still claim a row that does not exist,
 * and the old title is what a writer would mistake for this draft.
 */
function reviewChromeFromMerge(draft) {
  const payload = draft && typeof draft === 'object' ? draft : {};
  const hasSource = Object.prototype.hasOwnProperty.call(payload, 'source');
  const citationMap = payload.citation_map;
  const createdAt = typeof payload.created_at === 'string' && payload.created_at
    ? payload.created_at
    : null;
  return {
    summary_number: finiteNumber(payload.summary_number),
    message_count: finiteNumber(payload.message_count),
    created_at: createdAt,
    source: hasSource ? (payload.source ?? null) : null,
    content: payload.content || '',
    citation_map: (
      citationMap && typeof citationMap === 'object' && !Array.isArray(citationMap)
    ) ? citationMap : {},
  };
}

export function SessionSummaryModal({ isOpen, onClose, sessionId, onCitationClick, autoMerge = false }) {
  const [summaries, setSummaries] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editOriginal, setEditOriginal] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);   // head provenance + staleness
  const [refreshing, setRefreshing] = useState(false);
  // Unsaved merge review. Null while the saved head is what's on screen.
  const [reviewSummary, setReviewSummary] = useState(null);
  // True once the auto merge attempt has settled, so Cancel can show the
  // saved head again instead of holding the panel on the wait state.
  const [mergeFinished, setMergeFinished] = useState(false);
  const mergeToken = useRef(0);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen && sessionId) {
      fetchSummaries();
    }
    if (!isOpen) {
      // Drop an in-flight merge and the saved index so the next open does
      // not treat "back to the head" as a user switch that cancels the new
      // request.
      mergeToken.current += 1;
      setMergeFinished(false);
      setReviewSummary(null);
      setRefreshing(false);
      setIsEditing(false);
      setEditContent('');
      setEditOriginal('');
      setSelectedIndex(0);
    }
  }, [isOpen, sessionId]);

  // A dropdown change is a different saved row. A merge for the previous
  // row must not land on it. `isOpen` is read but not a dependency: opening
  // must not count as a switch. The index is reset while the modal is closed.
  useEffect(() => {
    if (!isOpen) return;
    mergeToken.current += 1;
    setRefreshing(false);
    setIsEditing(false);
    setEditContent('');
    setEditOriginal('');
    setReviewSummary(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const fetchSummaries = async () => {
    setLoading(true);
    setError(null);
    setSummaries([]);
    setSelectedIndex(0);
    setStatus(null);
    setReviewSummary(null);

    let st = null;
    try {
      let data;
      [data, st] = await Promise.all([
        sessionsApi.getAllSummaries(sessionId),
        sessionsApi.getSummaryStatus(sessionId).catch(() => null),
      ]);
      setStatus(st);
      if (data && data.length > 0) {
        setSummaries(data);
        setSelectedIndex(0); // Select the most recent (first) by default
      } else {
        setError(emptySummaryCopy(st));
      }
    } catch (err) {
      // Treat 404 and 500 as "no summary yet" - the session might not have any summaries
      if (err.status === 404 || err.status === 500) {
        setError(emptySummaryCopy(st));
      } else {
        setError(err.message || 'Failed to load summaries');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedSummary = summaries[selectedIndex];

  const handleStartEdit = () => {
    if (selectedSummary) {
      setEditContent(selectedSummary.content);
      setEditOriginal(selectedSummary.content);
      setIsEditing(true);
      // Focus textarea after render
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const handleRefresh = async () => {
    const token = mergeToken.current + 1;
    mergeToken.current = token;
    const saved = selectedSummary;
    setRefreshing(true);
    setError(null);
    try {
      // No summary body is sent. The server loads the latest saved gist,
      // which a writer edit is, and merges from that text.
      const draft = await sessionsApi.mergeDraftSummary(sessionId);
      if (token !== mergeToken.current) return;
      // Chrome comes only from the response. The saved head stays in
      // `summaries` so Cancel can put it back without another write.
      setReviewSummary(reviewChromeFromMerge(draft));
      setEditOriginal(saved ? saved.content : '');
      setEditContent((draft && draft.content) || '');
      setIsEditing(true);
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (err) {
      if (token !== mergeToken.current) return;
      setError(err.message || 'Failed to generate a merged draft');
    } finally {
      if (token === mergeToken.current) {
        setRefreshing(false);
        setMergeFinished(true);
      }
    }
  };

  // autoMerge: opened from the manual "Create Summary" button over a
  // writer_edited head — go straight into the merge-draft review once the
  // loaded head is that edit. Retries if a dropdown reset invalidated the
  // in-flight request before it could land.
  useEffect(() => {
    const headEdited = summaries[0]?.source === 'writer_edited'
      || status?.head_source === 'writer_edited';
    if (
      autoMerge && !loading && !error && headEdited
      && !isEditing && !reviewSummary && !refreshing && !mergeFinished
    ) {
      handleRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMerge, loading, status, isEditing, summaries, error, reviewSummary, refreshing, mergeFinished]);

  const handleCancelEdit = () => {
    mergeToken.current += 1;
    setIsEditing(false);
    setEditContent('');
    setEditOriginal('');
    setReviewSummary(null);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;

    setSaving(true);
    try {
      // Append-only: writer edit creates a NEW writer_edited version (head).
      await sessionsApi.writerEditSummary(sessionId, editContent.trim());
      setIsEditing(false);
      setEditContent('');
      setEditOriginal('');
      setReviewSummary(null);
      // Re-fetch so the new head version + updated status appear.
      await fetchSummaries();
    } catch (err) {
      console.error('Failed to save summary:', err);
      setError(err.message || 'Failed to save summary');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // While a merge is being prepared, paint neither the saved title nor the
  // saved text. Waiting starts as soon as this open is a merge — including
  // the frame before status returns and the whole request — so a cached
  // head cannot sit on screen for the model call.
  const headEdited = summaries[0]?.source === 'writer_edited'
    || status?.head_source === 'writer_edited';
  const loadedNotAWriterEdit = !loading && (
    Boolean(error)
    || (summaries.length === 0 && status != null && status.head_source !== 'writer_edited')
    || (Boolean(summaries[0]) && !headEdited)
  );
  const openedForMerge = Boolean(
    autoMerge && !mergeFinished && !reviewSummary && !isEditing
  );
  const showMergeWait = refreshing || (openedForMerge && !loadedNotAWriterEdit);
  const chrome = reviewSummary || selectedSummary;
  const unsavedDraft = Boolean(reviewSummary) && reviewSummary.summary_number == null;

  const getTitle = () => {
    if (showMergeWait || unsavedDraft) return 'Unsaved draft';
    const row = reviewSummary || selectedSummary;
    if (!row || row.summary_number == null) return 'Session Summary';
    if (row.summary_number === 0) {
      return 'Final Session Summary';
    }
    return `Interim Summary #${row.summary_number}`;
  };

  const isFinalSummary = chrome?.summary_number === 0;
  // Only the HEAD (most recent, index 0) is editable — historical versions
  // are read-only (spec: editing non-head versions is out of scope).
  const isHead = selectedIndex === 0;
  const isWriterEdited = chrome?.source === 'writer_edited';
  const canEdit = isHead && !isFinalSummary;
  const showRefresh = canEdit && status?.refresh_due;
  const detached = isEditing ? detachedCitations(editOriginal, editContent) : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={getTitle()} size="wide">
      <div className="session-summary-modal">
        {(loading || showMergeWait) ? (
          <Loading text={showMergeWait ? 'Preparing…' : 'Loading summaries...'} />
        ) : error ? (
          <div className="session-summary-modal__error">
            <span className="session-summary-modal__error-icon">ℹ️</span>
            <p>{error}</p>
          </div>
        ) : selectedSummary ? (
          <>
            {/* Summary selector dropdown */}
            {summaries.length > 1 && !reviewSummary && (
              <div className="session-summary-modal__selector">
                <label htmlFor="summary-select">View summary:</label>
                <select
                  id="summary-select"
                  value={selectedIndex}
                  onChange={(e) => setSelectedIndex(parseInt(e.target.value, 10))}
                  className="session-summary-modal__dropdown"
                >
                  {summaries.map((summary, index) => (
                    <option key={summary.id} value={index}>
                      {formatSummaryLabel(summary)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Type / provenance badges. An unsaved draft has no saved row
                to badge; the title is the whole identity. */}
            {!unsavedDraft && (
            <div className="session-summary-modal__badges">
              {!isFinalSummary && (
                <span className="session-summary-modal__type-badge">
                  Interim Summary (Session in progress)
                </span>
              )}
              {isWriterEdited && (
                <span className="session-summary-modal__badge session-summary-modal__badge--writer">
                  ✍️ Your edited version
                </span>
              )}
              {isHead && showRefresh && !isEditing && (
                <span className="session-summary-modal__badge session-summary-modal__badge--stale">
                  May be out of date · {status.staleness_delta} new message{status.staleness_delta === 1 ? '' : 's'} since your edit
                </span>
              )}
            </div>
            )}

            {/* Summary metadata with edit/refresh actions.
                Omit the bar entirely when the draft has no saved fields,
                so an empty pill is not read as the old row's header. */}
            {(chrome.message_count != null || chrome.created_at || (!isEditing && (showRefresh || canEdit))) && (
            <div className="session-summary-modal__meta">
              {chrome.message_count != null && (
              <span className="session-summary-modal__meta-item">
                <strong>Messages:</strong> {chrome.message_count}
              </span>
              )}
              {chrome.created_at && (
              <span className="session-summary-modal__meta-item">
                <strong>Created:</strong> {new Date(chrome.created_at).toLocaleString()}
              </span>
              )}
              {!isEditing && showRefresh && (
                <button
                  className="session-summary-modal__refresh-btn"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Merge new material into your edit for review"
                >
                  {refreshing ? 'Preparing…' : '↻ Refresh with new material'}
                </button>
              )}
              {!isEditing && canEdit && (
                <button
                  className="session-summary-modal__edit-btn"
                  onClick={handleStartEdit}
                  title="Edit this summary"
                >
                  ✏️ Edit
                </button>
              )}
            </div>
            )}

            {/* Summary content - view or edit mode */}
            {isEditing ? (
              <div className="session-summary-modal__edit-container">
                {/* Consequences, not just a warning (spec §4) */}
                <div className="session-summary-modal__consequences">
                  <strong>This summary is memory.</strong> It feeds your Book
                  Cards, Dossiers, and what Brainstormy recalls in future
                  sessions. Saving stores this as <em>your</em> version — a
                  higher-trust, writer-authored summary that supersedes the AI
                  one in retrieval. Prior versions are kept for reference.
                  {detached.length > 0 && (
                    <div className="session-summary-modal__detach">
                      Citations you removed will be detached:{' '}
                      {detached.map((m, i) => (
                        <span key={i} className="session-summary-modal__detach-tag">{m}</span>
                      ))}
                    </div>
                  )}
                </div>
                <textarea
                  ref={textareaRef}
                  className="session-summary-modal__edit-textarea"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  disabled={saving}
                  placeholder="Enter summary content..."
                />
                <div className="session-summary-modal__edit-actions">
                  <button
                    className="session-summary-modal__edit-action session-summary-modal__edit-action--cancel"
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="session-summary-modal__edit-action session-summary-modal__edit-action--save"
                    onClick={handleSaveEdit}
                    disabled={saving || !editContent.trim()}
                  >
                    {saving ? 'Saving...' : 'Save as my version'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="session-summary-modal__content">
                {renderContentWithCitations(
                  selectedSummary.content,
                  onCitationClick,
                  selectedSummary.citation_map,
                )}
              </div>
            )}
          </>
        ) : null}
      </div>
    </Modal>
  );
}

export default SessionSummaryModal;
